import { buttonStyle } from 'components/buttons/buttonStyle';
import { HistoryTimeline } from 'components/history/HistoryTimeline';
import { useFormatDate } from 'components/misc/FormattedDate';
import { Button } from 'components/ui/button';
import { ClientIdentificationDto, ClientIdentificationPanelDto } from 'types/clientIdentification';
import { t } from 'utils/getTranslations';
import { missingItemLabel } from '../missingItems';
import { Value, VALUE_GRID_CLASS } from './Block';
import { BLOCK_IDS, BLOCK_OF_MISSING, BlockId } from './blockIds';

const SUBHEADING_CLASS = 'text-label text-muted-foreground m-0 uppercase';

interface Props {
    assessment: ClientIdentificationDto;
    panel: ClientIdentificationPanelDto;
    onJump: (block: BlockId) => void;
}

export function ReviewBlock({ assessment, panel, onJump }: Props) {
    const formatDate = useFormatDate();
    if (assessment.statusShown !== 'DRAFT') {
        return (
            <>
                <div className={VALUE_GRID_CLASS}>
                    <Value label={t('client-identification.received-on')}>{formatDate(assessment.receivedOn)}</Value>
                    <Value label={t('client-identification.recorded-by')}>{assessment.receivedByName}</Value>
                </div>
                {assessment.history.length ? (
                    <div>
                        <p className={SUBHEADING_CLASS}>
                            {t('client-identification.history', { count: String(assessment.history.length) })}
                        </p>
                        <HistoryTimeline
                            entries={assessment.history.map((event, index) => ({
                                key: index,
                                tone: event.type === 'RECEIVED' ? 'success' : 'neutral',
                                children: (
                                    <>
                                        <div className="text-body2 font-semibold">
                                            {t(`client-identification.history-event.${event.type}`, {
                                                name: event.employeeName ?? '',
                                                date: formatDate(event.occurredAt) ?? '',
                                            })}
                                        </div>
                                        {event.comment ? (
                                            <div className="text-body2 text-fg-secondary whitespace-pre-wrap">
                                                {event.comment}
                                            </div>
                                        ) : null}
                                    </>
                                ),
                            }))}
                        />
                    </div>
                ) : null}
            </>
        );
    }

    const gaps = [
        ...panel.missingItems.map((item, index) => ({
            key: `item-${index}`,
            label: missingItemLabel(item, panel),
            block: BLOCK_OF_MISSING[item.block],
        })),
        ...panel.missingMandatoryRoles.map((role) => ({
            key: `role-${role.roleCode}`,
            label: t('client-identification.no-role-listed', { role: role.namePlural }),
            block: BLOCK_IDS.relatedParties,
        })),
    ];
    return (
        <>
            <div className="flex flex-col gap-2">
                <p className={SUBHEADING_CLASS}>{t('client-identification.still-missing')}</p>
                {gaps.length ? (
                    <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
                        {gaps.map((gap) => (
                            <li key={gap.key} className="text-body1 flex items-center gap-2">
                                <span className="flex-auto">{gap.label}</span>
                                <Button
                                    {...buttonStyle({ variant: 'text', size: 'extraSmall' })}
                                    onClick={() => onJump(gap.block)}
                                    aria-label={t('client-identification.go-to-named', { item: gap.label })}
                                >
                                    {t('client-identification.go-to')}
                                </Button>
                            </li>
                        ))}
                    </ul>
                ) : (
                    <p className="text-body1 m-0">{t('client-identification.nothing-missing')}</p>
                )}
            </div>
            <div className="flex flex-col gap-1.5">
                <p className={SUBHEADING_CLASS}>{t('client-identification.receiving-will')}</p>
                <ul className="text-body1 m-0 flex flex-col gap-0.5 ps-[18px]">
                    <li>{t('client-identification.receiving-lock')}</li>
                    <li>{t('client-identification.receiving-record')}</li>
                    <li>{t('client-identification.receiving-change-nothing')}</li>
                </ul>
            </div>
            <p className="text-caption text-foreground-secondary m-0">{t('client-identification.receiving-note')}</p>
        </>
    );
}
